import { getCargasEmTransitoDeposito } from "@spark/services";
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Card, CardBody, CardTitle, CardSubhead } from '@spark/ui-components/components/Card';
import { Button as CustomButton } from '@spark/ui-components/components/Button';
import { H5, H6 } from '@spark/ui-components/components/Typography';
import Icon from '@spark/ui-components/components/Icon';
import TanStackTable from '@spark/ui-components/components/TanStackTable/TanStackTable';
import { getPrevisaoCargasColumns } from './tanStackColumns';
import styled from 'styled-components'; // Componentes styled para funcionalidades específicas

const FullscreenContainer = styled.div`
  ${props => props.isFullscreen && `
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 9999;
    overflow-y: auto;
    background-color: #ffffff;
    color: #121212;
    /*  padding: 20px;*/
    
    /* Estilo específico para a tabela em fullscreen */
    .tanstack-table {
      height: calc(100vh - 40px);
      max-height: none;
    }
    
    /* Container da tabela em fullscreen */
    .tanstack-table > div {
      max-height: calc(100vh - 60px) !important;
    }
    
    /* Header da tabela em fullscreen */
    .tanstack-table thead th {
      font-size: 1.1rem !important;
      padding: 16px 12px !important;
    }
    
    /* Células da tabela em fullscreen */
    .tanstack-table tbody td {
      font-size: 1.1rem !important;
      padding: 16px 12px !important;
    }
  `}
`;
const ControlButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;
const HeaderWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;
const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;
const RefreshCounter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 0.9rem;
  color: #6c757d;
  
  .icon {
    color: #007bff;
  }
  
  .time {
    font-weight: 600;
    color: #495057;
    font-family: 'Courier New', monospace;
  }
`;
const TanStackTableWrapper = styled.div`
  ${props => props.isFullscreen && `
    height: calc(100vh - 60px);
    max-height: calc(100vh - 60px);
    
    /* Força a tabela a usar todo o espaço disponível */
    > div {
      height: 100% !important;
      max-height: 100% !important;
    }
    
    /* Container da tabela */
    > div > div {
      height: 100% !important;
      max-height: 100% !important;
    }
  `}
`;
const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutos

const PrevisaoCargasComponent = () => {
  const [cargaData, setCargaData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [useMock, setUseMock] = useState('true');
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [timeUntilRefresh, setTimeUntilRefresh] = useState(REFRESH_INTERVAL);
  const containerRef = useRef(null);
  const isMountedRef = useRef(true); // Cleanup quando componente é desmontado

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []); // Handler para mudança de fullscreen

  const handleFullscreenChange = () => {
    setIsFullscreen(!!document.fullscreenElement);
  };

  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []); // Toggle fullscreen da tabela

  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch(err => {
        console.error(`Erro ao entrar em tela cheia: ${err.message}`);
        alert(`Não foi possível entrar em tela cheia: ${err.message}. Verifique as permissões do navegador.`);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().then(() => setIsFullscreen(false)).catch(err => {
          console.error(`Erro ao sair da tela cheia: ${err.message}`);
        });
      }
    }
  }; // Busca de dados


  const fetchData = useCallback(async (isInitialLoad = false) => {
    if (!isMountedRef.current) return;

    if (isInitialLoad) {
      setLoading(true);
    }

    setError(null);

    try {
      const data = await getCargasEmTransitoDeposito({
        useMock
      });
      if (!isMountedRef.current) return;
      const dadosProcessados = Array.isArray(data) ? data : [];
      setCargaData(dadosProcessados);
      setTotalRegistros(dadosProcessados.length);
    } catch (err) {
      if (!isMountedRef.current) return;
      console.error(`❌ Falha ao buscar dados do A52, contate o suporte:`, err);
      setError(err.message);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [useMock]); // Effect para buscar dados e configurar intervalo

  useEffect(() => {
    // Busca inicial
    fetchData(true); // Configura intervalo de atualização automática

    const intervalId = setInterval(() => {
      console.log("Atualizando dados automaticamente...");
      fetchData(false);
    }, REFRESH_INTERVAL);
    return () => {
      clearInterval(intervalId);
      console.log("Intervalo de atualização limpo.");
    };
  }, []); // Removida dependência fetchData para evitar chamadas duplas
  // Effect para contador de tempo até próximo refresh

  useEffect(() => {
    const timerId = setInterval(() => {
      if (isMountedRef.current) {
        setTimeUntilRefresh(prev => {
          const newTime = prev - 1000; // Decrementa 1 segundo

          if (newTime <= 0) {
            return REFRESH_INTERVAL; // Reinicia o contador
          }

          return newTime;
        });
      }
    }, 1000); // Atualiza a cada segundo

    return () => {
      clearInterval(timerId);
    };
  }, []); // Função para formatar o tempo restante

  const formatTimeRemaining = milliseconds => {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor(milliseconds % 60000 / 1000);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }; // Função específica para refresh manual


  const handleRefresh = useCallback(async () => {
    if (!isMountedRef.current) return;
    setLoading(true);
    setError(null);
    console.log(`🔄 Iniciando refresh manual - Modo: ${useMock ? 'MOCK' : 'API'}`);

    try {
      const data = await getCargasEmTransitoDeposito({
        useMock
      });
      if (!isMountedRef.current) return;
      const dadosProcessados = Array.isArray(data) ? data : [];
      setCargaData(dadosProcessados);
      setTotalRegistros(dadosProcessados.length);
      console.log(`✅ Dados atualizados manualmente (${useMock ? 'MOCK' : 'API'}):`, dadosProcessados.length, "registros"); // Reseta o contador após refresh manual

      setTimeUntilRefresh(REFRESH_INTERVAL);
    } catch (err) {
      if (!isMountedRef.current) return;
      console.error(`❌ Erro ao atualizar dados (${useMock ? 'MOCK' : 'API'}):`, err);
      setError(err.message);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [useMock]); // Effect para recarregar dados quando o modo mock/API muda

  useEffect(() => {
    // Só executa se não for o carregamento inicial (isMountedRef.current já foi definido)
    if (isMountedRef.current) {
      console.log(`🔄 Modo alterado para ${useMock ? 'MOCK' : 'API'} - Recarregando dados...`);
      handleRefresh(); // Reseta o contador quando o modo muda

      setTimeUntilRefresh(REFRESH_INTERVAL);
    }
  }, [useMock, handleRefresh]);
  return <Container fluid>
      <Card>
        <CardBody>
          <HeaderWrap>
            <div>
              <CardTitle>Previsão de Cargas</CardTitle>
              <CardSubhead>
                Total de registros: {totalRegistros}
              </CardSubhead>
            </div>
            <ButtonContainer>
              {
              /* Botão modo mock */
            }
              <CustomButton variant={useMock ? "outline-warning" : "outline-success"} size="sm" onClick={() => setUseMock(!useMock)} title={useMock ? "Usando dados do mock" : "Usando dados da API"} aria-label="Alternar fonte de dados">
                
                <Icon name={useMock ? 'database-off' : 'database'} size={18} />
                {useMock ? 'Mock' : 'API'}
              </CustomButton>
              
              {
              /* Botão refresh */
            }
              <CustomButton variant="outline-primary" size="sm" onClick={handleRefresh} disabled={loading} title="Atualizar dados" aria-label="Atualizar dados">
                
                <Icon name="refresh" size={18} />
              </CustomButton>
              
              {
              /* Botão fullscreen */
            }
              <CustomButton variant="outline-info" size="sm" onClick={toggleFullscreen} title="Alternar tela cheia da tabela" aria-label="Alternar tela cheia">
                
                <Icon name={isFullscreen ? 'fullscreen-exit' : 'fullscreen'} size={18} />
              </CustomButton>
              
              {
              /* Contador de tempo até próximo refresh */
            }
              <RefreshCounter>
                <Icon name="calendar-clock" size={16} className="icon" />
                <span>Próximo refresh:</span>
                <span className="time">{formatTimeRemaining(timeUntilRefresh)}</span>
              </RefreshCounter>
            </ButtonContainer>
          </HeaderWrap>
          
          {error && cargaData.length > 0 && <Row className="mb-3">
              <Col md={12}>
                <div className="alert alert-warning text-center">
                  Falha ao atualizar os dados: {error}. Exibindo últimos dados carregados.
                </div>
              </Col>
            </Row>}
          
          
          <Row>
            <Col md={12}>
              <FullscreenContainer ref={containerRef} isFullscreen={isFullscreen}>
                
                <TanStackTableWrapper isFullscreen={isFullscreen}>
                  <TanStackTable columns={getPrevisaoCargasColumns()} data={cargaData} isSortable={true} withPagination={false} enableRowColors={false} loading={loading} className="tanstack-table" />
                  
                </TanStackTableWrapper>
              </FullscreenContainer>
            </Col>
          </Row>
        </CardBody>
      </Card>
    </Container>;
};

export default PrevisaoCargasComponent;
